import { useEffect, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import { auth, db } from "../../firebase/config";
import {
  IMPORTANT_USAGE_EVENTS,
  LOW_COST_MODE,
  shouldSkipMinorAnalyticsEvent,
  trackDailyRollup,
} from "../../utils/lowCostAnalytics";
import { recordAnalyticsEvent } from "../../utils/analyticsV2";
import { isLegacyAnalyticsEnabled } from "../../utils/analyticsLegacyFlags";

const SESSION_ID_KEY = "mfp_campaign_session_id";
const ATTRIBUTION_KEY = "mfp_campaign_attribution";
const LAST_EVENT_KEY = "mfp_last_campaign_event_key";
const LAST_USAGE_EVENT_PREFIX = "mfp_usage_event_";
const ACTIVE_FLOWS_KEY = "mfp_active_usage_flows";
const ANALYTICS_RULES_BLOCKED_KEY = "mfp_usage_analytics_rules_blocked";
const ANALYTICS_VALIDATION_WARNED_KEY = "mfp_usage_analytics_validation_warned";
const USAGE_DEDUPE_MS = 10 * 1000;
const ROUTE_TIME_MIN_MS = 4 * 1000;

export const USAGE_EVENT_TYPES = [
  "campaign_visit",
  "public_session_started",
  "public_page_view",
  "public_home_view",
  "public_marketplace_view",
  "public_product_open",
  "public_farm_stands_view",
  "public_stand_open",
  "public_live_shelf_view",
  "public_live_shelf_item_open",
  "public_events_view",
  "public_event_open",
  "public_jobs_view",
  "public_job_open",
  "public_plants_garden_view",
  "public_land_share_view",
  "public_land_share_open",
  "auth_gate_open",
  "signup_click",
  "signin_click",
  "request_to_buy_intent",
  "message_seller_intent",
  "job_apply_intent",
  "job_bid_intent",
  "event_participation_intent",
  "land_share_contact_or_post_intent",
  "home_explore_local_click",
  "home_marketplace_click",
  "home_farm_stands_click",
  "home_events_click",
  "home_jobs_click",
  "home_land_share_click",
  "home_plants_garden_click",
  "home_almanac_click",
  "home_live_shelf_click",
  "home_join_click",
  "route_view",
  "route_leave",
  "route_time_summary",
  "registration_started",
  "registration_completed",
  "email_verification_prompt_shown",
  "profile_edit_started",
  "profile_edit_saved",
  "profile_setup_completed",
  "farm_stand_created",
  "farm_stand_updated",
  "pickup_spot_created",
  "pickup_spot_updated",
  "stand_create_failed",
  "live_shelf_opened",
  "live_shelf_item_clicked",
  "request_to_buy_started",
  "request_to_buy_completed",
  "product_created",
  "product_updated",
  "inventory_quick_update",
  "sold_out_clicked",
  "payment_info_viewed",
  "job_viewed",
  "job_post_started",
  "job_post_completed",
  "farm_record_opened",
  "billy_opened",
  "billy_suggestion_clicked",
  "error_boundary_shown",
  "form_error_shown",
  "flow_abandoned",
];

const ANALYTICS_ALLOWED_FIELDS = [
  "createdAt",
  "eventType",
  "feature",
  "action",
  "status",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "path",
  "routeName",
  "durationMs",
  "flowId",
  "relatedId",
  "errorCode",
  "errorSummary",
  "referrer",
  "sessionId",
  "userId",
  "isLoggedIn",
];

const sanitizeValue = (value = "", maxLength = 120) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const sanitizeNumber = (value = 0, max = 24 * 60 * 60 * 1000) => {
  const next = Number(value);
  if (!Number.isFinite(next) || next < 0) return 0;
  return Math.min(Math.round(next), max);
};

const isLocalDev = () =>
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const getCanonicalTrackedPath = (pathname = "/") => {
  const cleanPath = String(pathname || "/").replace(/\/+$/, "") || "/";

  if (cleanPath === "/commercial-demo") return "/";
  if (cleanPath === "/Admin") return "/admin";
  if (cleanPath === "/browse") return "/browse-users";

  return cleanPath;
};

const getSessionId = () => {
  if (typeof window === "undefined") return "";

  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;

    const nextId =
      window.crypto?.randomUUID?.() ||
      `mfp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_ID_KEY, nextId);
    return nextId;
  } catch (error) {
    return `mfp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

const getUtmPayload = () => {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search || "");
  const source = sanitizeValue(params.get("utm_source"), 80);
  const medium = sanitizeValue(params.get("utm_medium"), 80);
  const campaign = sanitizeValue(params.get("utm_campaign"), 120);
  const content = sanitizeValue(params.get("utm_content"), 120);

  if (!source && !medium && !campaign && !content) return null;

  return {
    utm_source: source,
    utm_medium: medium,
    utm_campaign: campaign,
    utm_content: content,
    path: sanitizeValue(getCanonicalTrackedPath(window.location.pathname || "/"), 200),
    referrer: sanitizeValue(document.referrer || "", 300),
  };
};

const getStoredAttribution = () => {
  if (typeof window === "undefined") return null;

  try {
    const parsed = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || "null");
    if (!parsed?.sessionId || !parsed?.utm_campaign) return null;

    const capturedAt = parsed.capturedAt ? new Date(parsed.capturedAt).getTime() : 0;
    const isFresh = capturedAt && Date.now() - capturedAt <= 30 * 24 * 60 * 60 * 1000;
    if (!isFresh) return null;

    return {
      utm_source: sanitizeValue(parsed.utm_source, 80),
      utm_medium: sanitizeValue(parsed.utm_medium, 80),
      utm_campaign: sanitizeValue(parsed.utm_campaign, 120),
      utm_content: sanitizeValue(parsed.utm_content, 120),
      path: sanitizeValue(parsed.path || "/", 200),
      referrer: sanitizeValue(parsed.referrer || "", 300),
      sessionId: sanitizeValue(parsed.sessionId, 120),
    };
  } catch (error) {
    return null;
  }
};

const getRouteName = (pathname = "/") => {
  const cleanPath = getCanonicalTrackedPath(pathname);
  if (cleanPath === "/") return "Home";
  if (cleanPath === "/login") return "Public / Login";
  if (cleanPath === "/home") return "Home";
  if (cleanPath === "/marketplace") return "Marketplace";
  if (cleanPath.startsWith("/stand/")) return "Live Shelf";
  if (cleanPath.startsWith("/farm-stand") || cleanPath.startsWith("/add-farm-stand")) return "Farm Stand / Pickup Spot";
  if (cleanPath.startsWith("/profile")) return "Profile";
  if (cleanPath.startsWith("/edit-profile")) return "Edit Profile";
  if (cleanPath.startsWith("/jobs")) return "Jobs";
  if (cleanPath.startsWith("/farm-records") || cleanPath.startsWith("/records")) return "Farm Records";
  if (cleanPath.startsWith("/content-studio")) return "Content Studio";
  if (cleanPath.startsWith("/admin")) return "Admin";
  if (cleanPath.startsWith("/farm/")) return "Public Storefront";
  if (cleanPath.startsWith("/plants-garden")) return "Plants & Garden";
  return cleanPath;
};

const isPublicPath = (path = "") =>
  ["/", "/home", "/marketplace", "/farm-stands", "/events", "/jobs", "/plants-garden", "/land-share"].includes(path) ||
  path.startsWith("/stand/");

const getPublicRouteEvents = (path = "") => {
  const events = ["public_page_view"];
  if (path === "/" || path === "/home") return [...events, "public_home_view"];
  if (path === "/marketplace") return [...events, "public_marketplace_view"];
  if (path === "/farm-stands") return [...events, "public_farm_stands_view"];
  if (path.startsWith("/stand/")) return [...events, "public_stand_open", "public_live_shelf_view"];
  if (path === "/events") return [...events, "public_events_view"];
  if (path === "/jobs") return [...events, "public_jobs_view"];
  if (path === "/plants-garden") return [...events, "public_plants_garden_view"];
  if (path === "/land-share") return [...events, "public_land_share_view"];
  return events;
};

const getAttributionContext = () => {
  const direct = getUtmPayload();
  const stored = getStoredAttribution();
  return direct || stored || {};
};

const buildUsagePayload = (eventType, details = {}) => {
  const attribution = getAttributionContext();
  const userId = auth.currentUser?.uid || "";
  const path = sanitizeValue(
    getCanonicalTrackedPath(details.path || window.location.pathname || "/"),
    200
  );

  return {
    createdAt: serverTimestamp(),
    eventType: sanitizeValue(eventType, 80),
    feature: sanitizeValue(details.feature, 80),
    action: sanitizeValue(details.action, 80),
    status: sanitizeValue(details.status || "ok", 40),
    path,
    routeName: sanitizeValue(details.routeName || getRouteName(path), 80),
    durationMs: sanitizeNumber(details.durationMs),
    flowId: sanitizeValue(details.flowId, 120),
    relatedId: sanitizeValue(details.relatedId, 120),
    errorCode: sanitizeValue(details.errorCode, 80),
    errorSummary: sanitizeValue(details.errorSummary, 180),
    utm_source: sanitizeValue(attribution.utm_source, 80),
    utm_medium: sanitizeValue(attribution.utm_medium, 80),
    utm_campaign: sanitizeValue(attribution.utm_campaign, 120),
    utm_content: sanitizeValue(attribution.utm_content, 120),
    referrer: sanitizeValue(attribution.referrer || document.referrer || "", 300),
    sessionId: sanitizeValue(attribution.sessionId || getSessionId(), 120),
    userId,
    isLoggedIn: Boolean(userId),
  };
};

const validateAnalyticsPayload = (payload = {}) => {
  const allowed = new Set(ANALYTICS_ALLOWED_FIELDS);
  const extraFields = Object.keys(payload).filter((key) => !allowed.has(key));
  const invalidFields = [];

  if (extraFields.length) invalidFields.push(`extra fields: ${extraFields.join(", ")}`);
  if (!USAGE_EVENT_TYPES.includes(payload.eventType)) invalidFields.push("invalid eventType");
  ["feature", "action", "status", "path", "routeName", "flowId", "relatedId", "errorCode", "errorSummary", "utm_source", "utm_medium", "utm_campaign", "utm_content", "referrer", "sessionId", "userId"].forEach((field) => {
    if (typeof payload[field] !== "string") invalidFields.push(`${field} must be a string`);
  });
  if (typeof payload.isLoggedIn !== "boolean") invalidFields.push("isLoggedIn must be boolean");
  if (!Number.isFinite(payload.durationMs) || payload.durationMs < 0 || payload.durationMs > 86400000) {
    invalidFields.push("durationMs must be 0-86400000");
  }
  if (payload.isLoggedIn && !payload.userId) invalidFields.push("logged-in analytics need userId");
  if (!payload.isLoggedIn && payload.userId) invalidFields.push("anonymous analytics cannot include userId");

  return invalidFields;
};

const warnOnce = (storageKey, message, details) => {
  if (!isLocalDev()) return;
  try {
    if (window.sessionStorage.getItem(storageKey)) return;
    window.sessionStorage.setItem(storageKey, "true");
  } catch (error) {}
  if (details) {
    console.warn(message, details);
  } else {
    console.warn(message);
  }
};

const buildLegacyTrafficPayload = (payload = {}) => ({
  createdAt: serverTimestamp(),
  utm_source: sanitizeValue(payload.utm_source, 80),
  utm_medium: sanitizeValue(payload.utm_medium, 80),
  utm_campaign: sanitizeValue(payload.utm_campaign, 120),
  utm_content: sanitizeValue(payload.utm_content, 120),
  path: sanitizeValue(payload.path, 200),
  referrer: sanitizeValue(payload.referrer, 300),
  sessionId: sanitizeValue(payload.sessionId || getSessionId(), 120),
  userId: sanitizeValue(payload.userId, 128),
  isLoggedIn: Boolean(payload.isLoggedIn),
});

const getCanonicalEventForUsageEvent = (eventType, payload = {}) => {
  if (eventType === "public_session_started") return "session_start";
  if (eventType === "public_page_view") return "page_view";
  if (eventType === "registration_started") return "registration_start";
  if (eventType === "registration_completed") return "registration_complete";
  if (["flow_abandoned", "form_error_shown", "error_boundary_shown"].includes(eventType)) return "error";
  if (["request_to_buy_completed"].includes(eventType)) return "conversion";
  if (
    eventType.startsWith("public_") ||
    [
      "auth_gate_open",
      "live_shelf_opened",
      "job_viewed",
      "farm_record_opened",
      "billy_opened",
      "event_participation_intent",
    ].includes(eventType)
  ) {
    return payload.feature ? "feature_open" : "";
  }
  return "";
};

export const trackUsageEvent = async (eventType, details = {}) => {
  if (typeof window === "undefined" || !USAGE_EVENT_TYPES.includes(eventType)) return false;

  const payload = buildUsagePayload(eventType, details);
  const isAnonymous = !payload.userId;
  const canonicalEvent = getCanonicalEventForUsageEvent(eventType, payload);

  if (canonicalEvent) {
    recordAnalyticsEvent(canonicalEvent, {
      action: payload.action,
      campaign: payload.utm_campaign,
      errorCode: payload.errorCode,
      errorSummary: payload.errorSummary,
      feature: payload.feature,
      path: payload.path,
    });
  }

  if (LOW_COST_MODE) {
    if (eventType === "route_view" || eventType === "campaign_visit") {
      if (isLegacyAnalyticsEnabled("usageDailyRollups")) {
        trackDailyRollup(eventType, { oncePerDay: true });
      }
      return false;
    }

    if (eventType === "route_time_summary") {
      return false;
    }

    if (shouldSkipMinorAnalyticsEvent(eventType, isAnonymous)) {
      return false;
    }

    if (IMPORTANT_USAGE_EVENTS.has(eventType)) {
      if (isLegacyAnalyticsEnabled("usageDailyRollups")) {
        trackDailyRollup(eventType);
      }
    }
  }

  if (!isLegacyAnalyticsEnabled("campaignTrafficEvents")) {
    return false;
  }

  const validationErrors = validateAnalyticsPayload(payload);
  if (validationErrors.length) {
    warnOnce(
      ANALYTICS_VALIDATION_WARNED_KEY,
      "Usage analytics payload was not sent because it does not match the Firestore allowlist.",
      validationErrors
    );
    return false;
  }

  const dedupeKey = [
    payload.eventType,
    payload.feature,
    payload.action,
    payload.path,
    payload.relatedId,
    payload.userId || "anon",
  ].join("|");
  const storageKey = `${LAST_USAGE_EVENT_PREFIX}${dedupeKey}`;

  try {
    const last = Number(window.sessionStorage.getItem(storageKey) || 0);
    if (!details.allowRepeat && last && Date.now() - last < USAGE_DEDUPE_MS) {
      return false;
    }

    await addDoc(collection(db, "campaignTrafficEvents"), payload);
    window.sessionStorage.setItem(storageKey, String(Date.now()));
    return true;
  } catch (error) {
    if (error?.code === "permission-denied") {
      try {
        await addDoc(collection(db, "campaignTrafficEvents"), buildLegacyTrafficPayload(payload));
        window.sessionStorage.setItem(storageKey, String(Date.now()));
        warnOnce(
          ANALYTICS_RULES_BLOCKED_KEY,
          "Usage analytics details are blocked by the currently deployed Firestore rules. Deploy firestore.rules before the Vercel deploy to capture full usage events."
        );
        return true;
      } catch {
        warnOnce(
          ANALYTICS_RULES_BLOCKED_KEY,
          "Usage analytics writes are blocked by the currently deployed Firestore rules. Deploy firestore.rules before the Vercel deploy."
        );
        return false;
      }
    }
    console.warn("Usage analytics event was not recorded:", error.code || error.message);
    return false;
  }
};

const readActiveFlows = () => {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(ACTIVE_FLOWS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeActiveFlows = (flows = []) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(ACTIVE_FLOWS_KEY, JSON.stringify(flows.slice(-20)));
  } catch (error) {}
};

export const startTrackedFlow = (flowId, details = {}) => {
  if (typeof window === "undefined" || !flowId) return;
  const flows = readActiveFlows().filter((flow) => flow.flowId !== flowId);
  flows.push({
    flowId,
    feature: sanitizeValue(details.feature, 80),
    action: sanitizeValue(details.action || "start", 80),
    eventType: sanitizeValue(details.eventType, 80),
    path: sanitizeValue(getCanonicalTrackedPath(details.path || window.location.pathname || "/"), 200),
    routeName: sanitizeValue(details.routeName || getRouteName(window.location.pathname || "/"), 80),
    startedAt: Date.now(),
  });
  writeActiveFlows(flows);
};

export const completeTrackedFlow = (flowId) => {
  if (typeof window === "undefined" || !flowId) return;
  writeActiveFlows(readActiveFlows().filter((flow) => flow.flowId !== flowId));
};

export const flushAbandonedFlows = () => {
  if (typeof window === "undefined") return;
  const now = Date.now();
  const flows = readActiveFlows();
  const remaining = [];

  flows.forEach((flow) => {
    const durationMs = now - Number(flow.startedAt || now);
    if (durationMs < 15 * 1000) {
      remaining.push(flow);
      return;
    }

    trackUsageEvent("flow_abandoned", {
      feature: flow.feature,
      action: flow.action,
      status: "abandoned",
      path: flow.path,
      routeName: flow.routeName,
      flowId: flow.flowId,
      durationMs,
      allowRepeat: true,
    });
  });

  writeActiveFlows(remaining);
};

const CampaignTrafficTracker = () => {
  const location = useLocation();
  const routeRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user?.uid) return;
      try {
        if (window.sessionStorage.getItem("mfp_public_session_started")) return;
        window.sessionStorage.setItem("mfp_public_session_started", "true");
      } catch (error) {}
      trackUsageEvent("public_session_started", {
        feature: "public",
        action: "session_start",
        path: getCanonicalTrackedPath(window.location.pathname || "/"),
      });
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const utmPayload = getUtmPayload();
    const storedAttribution = getStoredAttribution();
    if (!utmPayload && !storedAttribution) return undefined;

    let cancelled = false;
    const sessionId = utmPayload ? getSessionId() : storedAttribution.sessionId;

    if (utmPayload) {
      try {
        window.localStorage.setItem(
          ATTRIBUTION_KEY,
          JSON.stringify({
            ...utmPayload,
            sessionId,
            capturedAt: new Date().toISOString(),
          })
        );
      } catch (error) {
        // Attribution storage is helpful, but tracking should stay non-blocking.
      }
    }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;

      const userId = user?.uid || "";
      const payload = utmPayload || (userId ? {
        ...storedAttribution,
        path: sanitizeValue(
          getCanonicalTrackedPath(window.location.pathname || storedAttribution.path || "/"),
          200
        ),
      } : null);
      if (!payload) return;

      if (LOW_COST_MODE) {
        if (isLegacyAnalyticsEnabled("usageDailyRollups")) {
          trackDailyRollup("campaign_visit", { oncePerDay: true });
        }
        return;
      }

      if (!isLegacyAnalyticsEnabled("campaignAttributionEvents")) {
        return;
      }

      const eventKey = utmPayload
        ? `${sessionId}|${userId || "anon"}|${payload.path}|${window.location.search}`
        : `${sessionId}|${userId}|stored-attribution`;

      try {
        if (window.localStorage.getItem(LAST_EVENT_KEY) === eventKey) {
          return;
        }

        await addDoc(collection(db, "campaignTrafficEvents"), {
          ...payload,
          eventType: "campaign_visit",
          feature: "campaign",
          action: "visit",
          status: "ok",
          routeName: getRouteName(payload.path),
          durationMs: 0,
          flowId: "",
          relatedId: "",
          errorCode: "",
          errorSummary: "",
          sessionId,
          userId,
          isLoggedIn: Boolean(userId),
          createdAt: serverTimestamp(),
        });

        window.localStorage.setItem(LAST_EVENT_KEY, eventKey);
      } catch (error) {
        if (error?.code === "permission-denied") {
          try {
            await addDoc(
              collection(db, "campaignTrafficEvents"),
              buildLegacyTrafficPayload({
                ...payload,
                sessionId,
                userId,
                isLoggedIn: Boolean(userId),
              })
            );
            window.localStorage.setItem(LAST_EVENT_KEY, eventKey);
            warnOnce(
              ANALYTICS_RULES_BLOCKED_KEY,
              "Campaign analytics details are blocked by the currently deployed Firestore rules. Deploy firestore.rules before the Vercel deploy to capture full campaign events."
            );
            return;
          } catch {
            warnOnce(
              ANALYTICS_RULES_BLOCKED_KEY,
              "Campaign analytics writes are blocked by the currently deployed Firestore rules. Deploy firestore.rules before the Vercel deploy."
            );
            return;
          }
        }
        console.warn("Campaign traffic event was not recorded:", error.code || error.message);
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const path = getCanonicalTrackedPath(location.pathname || "/");
    const routeName = getRouteName(path);
    routeRef.current = {
      path,
      routeName,
      startedAt: Date.now(),
      flushed: false,
    };

    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (cancelled) return;

      if (!user?.uid && isPublicPath(path)) {
        getPublicRouteEvents(path).forEach((eventType) => {
          trackUsageEvent(eventType, {
            feature: path.startsWith("/stand/") ? "live_shelf" : routeName.toLowerCase(),
            action: "view",
            path,
            routeName,
          });
        });
      }

      trackUsageEvent("route_view", {
        feature: "navigation",
        action: "view",
        path,
        routeName,
      });
    });

    const flushRouteTime = (reason = "route_leave") => {
      const route = routeRef.current;
      if (!route || route.flushed || route.path !== path) return;
      route.flushed = true;

      const durationMs = Date.now() - route.startedAt;
      if (durationMs >= ROUTE_TIME_MIN_MS) {
        trackUsageEvent("route_time_summary", {
          feature: "navigation",
          action: reason,
          path: route.path,
          routeName: route.routeName,
          durationMs,
          allowRepeat: true,
        });
      }

      flushAbandonedFlows();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        flushRouteTime("hidden");
      }
    };

    const handlePageHide = () => flushRouteTime("pagehide");

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      cancelled = true;
      unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      flushRouteTime("route_leave");
    };
  }, [location.pathname]);

  return null;
};

export default CampaignTrafficTracker;
